// Browser-session auth voor /v2 dashboard.
//
// Backend zet HttpOnly+Secure+SameSite-Strict cookie die JS niet kan lezen
// — het frontend "weet" dat je ingelogd bent door /api/auth/status te
// pollen, niet door de cookie zelf te inspecteren. Alle write-fetches
// gebruiken `credentials: 'include'` zodat de browser de cookie automatisch
// meestuurt.
//
// Wachtwoord-flow: bij `has_password=false` toont frontend SetupScreen waar
// gebruiker eenmalig API-key + nieuw wachtwoord invoert. Daarna login met
// alleen wachtwoord (browser-password-manager onthoudt 'm).

const PATHS = {
  hasPassword: '/api/auth/has-password',
  setupPassword: '/api/auth/setup-password',
  changePassword: '/api/auth/change-password',
  login: '/api/auth/login',
  logout: '/api/auth/logout',
  status: '/api/auth/status',
};

export interface AuthStatus {
  authed: boolean;
  // v36 (2026-07-25): multi-user auth — frontend routes/UI can gate op rol.
  // Backward-compat: oude clients die alleen `authed` lezen blijven werken.
  username?: string | null;
  is_admin?: boolean;
}

export interface HasPasswordStatus {
  has_password: boolean;
}

export interface LoginResult {
  authed: boolean;
  expires_at_utc?: string;
  username?: string | null;
  is_admin?: boolean;
}

export async function fetchAuthStatus(): Promise<AuthStatus> {
  const res = await fetch(PATHS.status, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return { authed: false };
  return (await res.json()) as AuthStatus;
}

export async function fetchHasPassword(): Promise<HasPasswordStatus> {
  const res = await fetch(PATHS.hasPassword, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return { has_password: false };
  return (await res.json()) as HasPasswordStatus;
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
}

export async function loginWithPassword(
  password: string,
  remember: boolean,
): Promise<LoginResult> {
  const res = await postJson(PATHS.login, { password, remember });
  if (res.status === 401) throw new Error('Onjuist wachtwoord');
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Login faalde: ${res.status} ${body.slice(0, 120)}`);
  }
  return (await res.json()) as LoginResult;
}

export async function setupPassword(
  apiKey: string,
  newPassword: string,
): Promise<LoginResult> {
  const res = await postJson(PATHS.setupPassword, {
    key: apiKey,
    new_password: newPassword,
  });
  if (res.status === 401) throw new Error('Onjuiste API-key');
  if (res.status === 400) {
    const body = await res.text().catch(() => '');
    throw new Error(body.includes('karakters') ? 'Wachtwoord moet ≥ 8 karakters zijn' : 'Ongeldige invoer');
  }
  if (!res.ok) {
    throw new Error(`Setup faalde: ${res.status}`);
  }
  return (await res.json()) as LoginResult;
}

export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await postJson(PATHS.changePassword, {
    old_password: oldPassword,
    new_password: newPassword,
  });
  if (res.status === 401) throw new Error('Huidig wachtwoord onjuist');
  if (res.status === 400) throw new Error('Wachtwoord moet ≥ 8 karakters zijn');
  if (!res.ok) throw new Error(`Wijziging faalde: ${res.status}`);
}

export async function logout(): Promise<void> {
  await fetch(PATHS.logout, {
    method: 'POST',
    credentials: 'include',
  });
}
